import { isNonEmpty } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import { ValidationErrorComponent } from 'components/common-form/sidePanel/status/validationErrors/ValidationErrors.status';
import { WithErrorTooltip } from 'components/shared/WithErrorTooltip/WithErrorTooltip';
import type { IntlKey } from 'locales';
import type { FormError } from 'models/formError';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import type { QuoteWarning } from 'models/QuoteWarning';
import { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { ExposedFormStatus } from 'state/forms/models';
import { isOrdersBasketFormTimeoutStatus } from 'state/orders-basket/helpers';

import { FormStatusIcon } from '../../shared/FormStatusIcon/FormStatusIcon';
import { FormStatusIconWithTooltip } from '../../shared/FormStatusIconWithTooltip/FormStatusIconWithTooltip';

export interface OrdersBasketStatusIconProps {
  errors?: readonly NextWorkflowError[];
  warnings?: readonly QuoteWarning[];
  validationErrors: readonly FormError[];
  formId: string;
  formJsonRepresentation?: string;
  exposedStatus?: ExposedFormStatus;
  isVisible: boolean;
}

export const OrdersBasketStatusIcon: FC<OrdersBasketStatusIconProps> = ({
  errors,
  warnings,
  validationErrors,
  formJsonRepresentation,
  exposedStatus,
  formId,
  isVisible,
}) => {
  const { formatMessage } = useIntl();

  const hasTimeoutError = isOrdersBasketFormTimeoutStatus(exposedStatus);
  const hasError = isNonEmpty(errors);
  const hasWarning = isNonEmpty(warnings);
  const hasValidationError = isNonEmpty(validationErrors);

  const overrideErrorMessages = useMemo(
    () => (hasTimeoutError ? [`form.status.Timeout.${exposedStatus}`] : undefined),
    [hasTimeoutError, exposedStatus],
  );
  const errorTitle = useMemo(() => getTooltipTitleTranslationKey(exposedStatus), [exposedStatus]);
  const errorContentToCopy = useMemo(
    () =>
      hasTimeoutError
        ? formatMessage({
            id: `form.status.Timeout.${exposedStatus}` as any,
            defaultMessage: 'form.status.Timeout.undefined',
          })
        : formJsonRepresentation,
    [hasTimeoutError, exposedStatus, formJsonRepresentation, formatMessage],
  );
  if (isVisible && (hasError || hasWarning || hasTimeoutError)) {
    return (
      <WithErrorTooltip
        errors={errors}
        warnings={warnings}
        overrideErrorMessages={overrideErrorMessages}
        errorTitle={errorTitle}
        position="right"
        canCopy
        contentToCopy={errorContentToCopy}
      >
        <FormStatusIcon status={exposedStatus} />
      </WithErrorTooltip>
    );
  }

  const TooltipContent = (
    <div className="p-2 text-primary text-start bg-lvl1 shadow border">
      <h3 className="fs-14 mb-3">
        <FormattedMessage id="form.status.NotReady" />
      </h3>
      <ValidationErrorComponent
        validationErrors={validationErrors}
        hiddenFields={[]}
        onlyErrorsDisplayedInSummary={false}
        errorPrefix="quote.errorMessages."
        className="pb-2"
      />
    </div>
  );

  return hasValidationError ? (
    <Tooltip position="right" contents={TooltipContent} style={{ padding: 0 }}>
      <div>
        <FormStatusIcon status={exposedStatus} />
      </div>
    </Tooltip>
  ) : (
    <FormStatusIconWithTooltip
      tooltipId={`orders-basket-form-${formId}-status-icon`}
      tooltipLocation="right"
      status={exposedStatus}
    />
  );
};

function getTooltipTitleTranslationKey(exposedStatus?: ExposedFormStatus): IntlKey {
  switch (exposedStatus) {
    case 'QuoteTimeout':
    case 'TradeAuthorizationTimeout':
    case 'TradeTimeout':
      return 'form.status.Timeout';
    default:
      return `form.status.${exposedStatus}` as any;
  }
}
